import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import {
  Trophy,
  Star,
  PlusCircle,
  FlaskConical,
  Award,
  Target,
  Medal,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@/common/components/ui/chart";
import { format } from "date-fns";
import { GoLinkExternal } from "react-icons/go";
import {
  PlatformPageShell,
  PlatformPageHeader,
  VerifyBanner,
  EmptyConnectState,
  StatTilesRow,
  PlatformInternalScoreCard,
  SectionCard,
  cardClass,
  getPlatformMeta,
  buildContestPlatformBreakdown,
  connectProfilePath,
  formatNumber,
} from "./platformStatsShared";

const chartConfig = {
  visitors: {
    label: "Rating Progress",
  },
  desktop: {
    label: "Rating",
    color: "hsl(var(--chart-1))",
  },
};

const StatsCard = ({ title, value, icon }) => (
  <div className="p-6 transition-all bg-white rounded-lg shadow-sm hover:shadow-md">
    <div className="flex flex-col gap-1">
      <div className="flex items-start justify-between">
        <span className="text-sm text-gray-600">{title}</span>
        {icon}
      </div>
      <span className="mt-2 text-2xl font-semibold">{value}</span>
    </div>
  </div>
);

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Codeforces = () => {
  const navigate = useNavigate();
  const { user, getLatestToken } = useAuth();
  const currentUser = user?.profile;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const token = await getLatestToken();
      if (!token) throw new Error("Missing auth token");
      const response = await fetch(
        `${
          import.meta.env.VITE_CLIENT_FUNCTION_BASE_URL
        }/client_dashboard/codeforces`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();

      if (result.status === "success") {
        setData(result.data);
      } else {
        console.error("Error in fetching data", result);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const refreshUser = async () => {
    try {
      setRefreshing(true);
      const res = await fetch(
        `${
          import.meta.env.VITE_CLIENT_FUNCTION_BASE_URL
        }/codeforces/refresh_user/${currentUser?.id}`,
        {
          method: "POST",
        }
      );

      if (!res.ok) {
        throw new Error("Error in refreshing user");
      }

      fetchData();
    } catch (error) {
      console.error("Error in refreshing user", error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  if (loading) {
    return (
      <div className="flex flex-col gap-6 w-full max-w-[1200px] mx-auto p-4 animate-pulse">
        {/* Verification Banner Skeleton */}
        <div className="flex items-center justify-between p-6 rounded-lg bg-gradient-to-r from-slate-700/20 to-slate-900/20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
            <div>
              <div className="w-48 h-6 mb-2 bg-gray-200 rounded"></div>
              <div className="w-64 h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div className="w-24 h-10 bg-gray-200 rounded"></div>
        </div>

        {/* Refresh Button Skeleton */}
        <div className="flex flex-col items-end w-full gap-1">
          <div className="w-32 h-10 bg-gray-200 rounded"></div>
          <div className="w-48 h-4 bg-gray-200 rounded"></div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="p-6 transition-all bg-white rounded-lg shadow-sm hover:shadow-md"
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-start justify-between">
                  <div className="w-32 h-4 bg-gray-200 rounded"></div>
                  <div className="w-5 h-5 bg-gray-200 rounded"></div>
                </div>
                <div className="w-24 h-8 mt-2 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Profile Details Card Skeleton */}
        <div className="grid grid-cols-1 gap-6 md:grid-rows-1">
          <Card className="flex-1 p-6 bg-white shadow-sm">
            <div className="mb-6">
              <div className="h-6 bg-gray-200 rounded w-36"></div>
            </div>
            <div className="space-y-6">
              {/* Metrics */}
              <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-4 rounded-lg bg-gray-50"
                  >
                    <div className="w-8 h-8 bg-gray-200 rounded"></div>
                    <div>
                      <div className="w-24 h-4 mb-2 bg-gray-200 rounded"></div>
                      <div className="w-16 h-6 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* User Info */}
              <div className="grid grid-cols-2 gap-8 pt-4">
                {[1, 2].map((i) => (
                  <div key={i}>
                    <div className="w-20 h-4 mb-2 bg-gray-200 rounded"></div>
                    <div className="w-32 h-6 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Rating Graph Card Skeleton */}
          <Card className="flex-1 p-6 bg-white shadow-sm">
            <div className="mb-6">
              <div className="h-6 bg-gray-200 rounded w-36"></div>
            </div>
            <div className="h-[300px] bg-gray-100 rounded-lg"></div>
          </Card>
        </div>

        {/* Contest History Table Skeleton */}
        <div className="p-6 bg-white rounded-lg shadow-sm">
          <div className="h-6 mb-6 bg-gray-200 rounded w-36"></div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  {[
                    "Contest",
                    "Problems Solved",
                    "Rank",
                    "Rating Change",
                    "New Rating",
                  ].map((header) => (
                    <th key={header} className="px-4 py-3 text-left">
                      <div className="w-24 h-4 mx-auto bg-gray-200 rounded"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4].map((i) => (
                  <tr key={i} className="border-b last:border-b-0">
                    <td className="px-4 py-3">
                      <div className="space-y-2">
                        <div className="w-48 h-5 bg-gray-200 rounded"></div>
                        <div className="w-32 h-4 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="w-16 h-5 mx-auto bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="w-16 h-5 mx-auto bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="w-16 h-5 mx-auto bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="w-16 h-5 mx-auto bg-gray-200 rounded"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <PlatformPageShell>
        <EmptyConnectState
          platformName="Codeforces"
          onConnect={() => navigate(connectProfilePath())}
        />
      </PlatformPageShell>
    );
  }

  const meta = getPlatformMeta("codeforces");
  const scoreBreakdown =
    data.scoreBreakdown || buildContestPlatformBreakdown(data, "codeforces");

  return (
    <PlatformPageShell>
      {!data?.verified ? (
        <VerifyBanner onVerify={() => navigate(connectProfilePath())} />
      ) : null}

      <PlatformPageHeader
        title="Codeforces"
        subtitle={`${formatNumber(data?.totalSolved ?? 0)} problems solved · ${formatNumber(data?.contestsAttended ?? 0)} contests`}
        username={data?.username}
        profileUrl={meta.profileUrl(data?.username)}
        lastSynced={data?.lastUpdated}
        refreshing={refreshing}
        onRefresh={refreshUser}
      />

      <StatTilesRow
        items={[
          {
            label: "Problems",
            value: data?.totalSolved ?? 0,
            hint: "solved",
            icon: FlaskConical,
            wrap: "border-sky-100 bg-linear-to-br from-sky-50 to-white",
            iconWrap: "bg-sky-100 text-sky-600",
          },
          {
            label: "Contests",
            value: data?.contestsAttended ?? 0,
            hint: "attended",
            icon: Award,
            wrap: "border-amber-100 bg-linear-to-br from-amber-50 to-white",
            iconWrap: "bg-amber-100 text-amber-600",
          },
          {
            label: "Rating",
            value: data?.currentRating ?? 0,
            hint: "current",
            icon: Trophy,
            wrap: "border-zinc-200/80 bg-white",
            iconWrap: "bg-indigo-50 text-indigo-600",
          },
          {
            label: "Max rating",
            value: data?.maxRating ?? 0,
            hint: data?.maxRank || "peak",
            icon: Target,
            wrap: "border-emerald-100 bg-linear-to-br from-emerald-50 to-white",
            iconWrap: "bg-emerald-100 text-emerald-600",
          },
        ]}
      />

      <PlatformInternalScoreCard slug="codeforces" scoreBreakdown={scoreBreakdown} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <SectionCard title="Profile details">
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-orange-50">
                <Trophy className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-sm text-gray-600">Current Rating</p>
                  <p className="text-xl font-bold">{data?.currentRating}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-indigo-50">
                <Target className="w-8 h-8 text-indigo-600" />
                <div>
                  <p className="text-sm text-gray-600">Max Rating</p>
                  <p className="text-xl font-bold">{data?.maxRating}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-yellow-50">
                <Medal className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-sm text-gray-600">Max Rank</p>
                  <p className="text-xl font-bold capitalize">
                    {data?.maxRank}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 pt-4">
              <div>
                <p className="mb-1 text-sm text-gray-600">Username</p>
                <p className="font-semibold flex items-center gap-2">
                  <span>{data?.username}</span>
                  {data?.username && (
                    <Link
                      className="group"
                      target="_blank"
                      to={`https://codeforces.com/profile/${data?.username}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <GoLinkExternal className="w-6 h-6 text-gray-400 group-hover:text-gray-700" />
                    </Link>
                  )}
                </p>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-600">Full Name</p>
                <p className="font-semibold">{data?.fullName}</p>
              </div>
            </div>
        </SectionCard>
        <SectionCard title="Rating progress">
          <div className="h-[300px]">
            <ChartContainer config={chartConfig} className="w-full h-full">
              <AreaChart data={data?.contests_participated || []}>
                <defs>
                  <linearGradient id="fillRating" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff9800" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#ff9800" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="created_at"
                  tickFormatter={formatDate}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                />
                <ChartTooltip
                  cursor={false}
                  content={<CustomTooltip indicator="dot" />}
                />
                <Area
                  type="monotone"
                  dataKey="rating"
                  stroke="#ff9800"
                  fill="url(#fillRating)"
                  strokeWidth={2}
                />
                {/* <ChartLegend content={<ChartLegendContent />} /> */}
              </AreaChart>
            </ChartContainer>
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Contest history">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b">
              <tr className="text-sm text-gray-600">
                <th className="px-4 py-3 text-left">Contest</th>
                <th className="px-4 py-3 text-center">Problems Solved</th>
                <th className="px-4 py-3 text-center">Rank</th>
                <th className="px-4 py-3 text-center">Rating Change</th>
                <th className="px-4 py-3 text-center">New Rating</th>
              </tr>
            </thead>
            <tbody>
              {data?.contests_participated?.map((contest) => (
                <tr
                  key={contest.id}
                  className="border-b last:border-b-0 hover:bg-gray-50"
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium">
                        {contest.codeforces_contest.title}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(contest.codeforces_contest.start_time)}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {contest.problems_solved}/
                    {contest.codeforces_contest.total_problems}
                  </td>
                  <td className="px-4 py-3 text-center">{contest.rank}</td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={
                        contest.rating_change >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {contest.rating_change >= 0 ? "+" : ""}
                      {contest.rating_change}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">{contest.rating}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </PlatformPageShell>
  );
};

export default Codeforces;

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload;

    return (
      <div className="p-2 text-sm text-gray-700 bg-white border rounded-lg shadow-sm">
        <div className="font-semibold text-black">
          {data.codeforces_contest.title}
        </div>
        <div>Rating: {data.rating}</div>
        <div className="text-xs text-gray-500">
          {formatDate(data.created_at)}
        </div>
      </div>
    );
  }

  return null;
};
